import { scalePaperMarksToCap } from "@/lib/assessment-marks"
import { readLivePaper } from "@/lib/assessment-live-paper"
import { readCreatedAssessments } from "@/lib/created-assessments"
import { sectionBlueprintPlan } from "@/lib/leo-blueprint-section-fill"
import { MOCK_ASSESSMENTS } from "@/lib/mock/exam-data"
import type { LibraryAiSpotted } from "@/lib/library-ai-spotted"
import {
  QUESTION_CATALOG,
  catalogPaperQuestion,
} from "@/lib/mock/question-catalog"

/**
 * Detail-page paper seeds.
 *
 * - Hub `a9` / course `*-a7` Extra Credit Quiz: ready 15-item paper. Two items
 *   in each section miss the blueprint so readiness is not 100%.
 * - Course `*-a5` Case Study Draft: the shared question catalog vs a
 *   20-question / 100-point target. Points, time, need-review, and off-spec
 *   rows still alert.
 * - Course `*-a8` Final Preview: partial draft — 8 ready items vs 25 target.
 * - Course `*-a9` Capstone Proposal: empty paper vs a 20-question / 100-point plan.
 * - Scheduled: healthy paper sized to the assessment count. Paper is locked
 *   and stays aligned; the strip must not show a negative quota.
 * - Other drafts: healthy paper sized to the assessment count, with one
 *   off-spec row per section.
 */

export const HEALTHY_DRAFT_ASSESSMENT_ID = "a9"
export const HEALTHY_DRAFT_ASSESSMENT_CODE = "ASM-109"
/** Course-offering Extra Credit Quiz slug (`NURS-210-a7`). */
export const HEALTHY_COURSE_OFFERING_SLUG = "a7"
/** Course-offering Case Study Draft slug (`NURS-210-a5`). */
export const RESEARCH_PAPER_SLUG = "a5"
/** Course-offering partial draft slug (`NURS-210-a8`). */
export const PARTIAL_COURSE_OFFERING_SLUG = "a8"
/** Course-offering empty draft slug (`NURS-210-a9`). */
export const EMPTY_COURSE_OFFERING_SLUG = "a9"

export interface AssessmentDetailPaperQuestion {
  id: string
  number: number
  stem: string
  title?: string
  type: string
  topic: string
  difficulty: "Easy" | "Medium" | "Hard" | ""
  difficultyPercentage: number
  pointBiserial: number
  discriminationIndex: number
  blooms: string
  points: number
  usedBefore: boolean
  usedCount: number
  usedInSameCourse: boolean
  sectionId?: string
  groupId?: string
  status?: "Draft" | "Published"
  /** Same AI Spotted tag as the question bank row. */
  aiSpotted?: LibraryAiSpotted
  /** What Leo changed when the tag is Looks good. */
  fixedSummary?: string
  /** Bank id, shared with the library catalog. */
  questionId?: string
}

function firstOnPlan<T extends string>(value: T, plan: readonly string[]): T {
  if (plan.length === 0 || plan.includes(value)) return value
  return (plan[0] ?? value) as T
}

/** Snap classification onto the section plan. Used for locked / scheduled papers. */
function snapToSectionPlan(
  row: AssessmentDetailPaperQuestion,
): AssessmentDetailPaperQuestion {
  const plan = sectionBlueprintPlan(row.sectionId ?? "")
  if (
    plan.topics.length === 0 &&
    plan.blooms.length === 0 &&
    plan.types.length === 0 &&
    plan.difficulties.length === 0
  ) {
    return row
  }
  return {
    ...row,
    topic: firstOnPlan(row.topic, plan.topics),
    blooms: firstOnPlan(row.blooms, plan.blooms),
    type: firstOnPlan(row.type, plan.types),
    difficulty: firstOnPlan(
      row.difficulty,
      plan.difficulties,
    ) as AssessmentDetailPaperQuestion["difficulty"],
  }
}

const DRAFT_ALIGNMENT_GAPS_PER_SECTION = 2

/** Classification misses so draft readiness is not 100%. */
function knockRowOffSpec(
  row: AssessmentDetailPaperQuestion,
  variant: number,
): AssessmentDetailPaperQuestion {
  if (row.sectionId === "A") {
    return variant === 0
      ? { ...row, topic: "Pharmacology" }
      : { ...row, blooms: "Creating" }
  }
  if (row.sectionId === "B") {
    return variant === 0
      ? { ...row, blooms: "Creating" }
      : { ...row, topic: "Cardiology" }
  }
  if (row.sectionId === "C") {
    return variant === 0
      ? { ...row, difficulty: "Hard" }
      : { ...row, topic: "Cardiology" }
  }
  return row
}

function withDraftAlignmentGaps(
  paper: AssessmentDetailPaperQuestion[],
): AssessmentDetailPaperQuestion[] {
  const counts = new Map<string, number>()
  return paper.map((row) => {
    const section = row.sectionId ?? ""
    if (!section) return row
    const seen = counts.get(section) ?? 0
    if (seen >= DRAFT_ALIGNMENT_GAPS_PER_SECTION) return row
    counts.set(section, seen + 1)
    return knockRowOffSpec(row, seen)
  })
}

/** Case-study paper. Same questions, status, and AI Spotted as the bank catalog. */
function researchPaperRows(): AssessmentDetailPaperQuestion[] {
  return QUESTION_CATALOG.map((row, index) => catalogPaperQuestion(row, index + 1))
}

export function defaultAssessmentPaper(): AssessmentDetailPaperQuestion[] {
  return scalePaperMarksToCap(withDraftAlignmentGaps(researchPaperRows()))
}

/** Few items on the paper: two sections started, one still empty. */
export function partialBlueprintDraftPaper(): AssessmentDetailPaperQuestion[] {
  const keep = new Set(["q5", "q16", "q4", "q7", "q15", "q8", "q14", "q13"])
  return withDraftAlignmentGaps(
    researchPaperRows()
      .filter((row) => keep.has(row.id))
      .map((row, index) => ({ ...row, number: index + 1 })),
  )
}

function okItem(
  row: Pick<AssessmentDetailPaperQuestion, "id" | "number" | "stem" | "title" | "topic" | "sectionId"> &
    Partial<AssessmentDetailPaperQuestion>,
): AssessmentDetailPaperQuestion {
  return {
    type: "Multiple Choice",
    difficulty: "Medium",
    difficultyPercentage: 60,
    pointBiserial: 0.32,
    discriminationIndex: 0.34,
    blooms: "Understanding",
    points: 5,
    usedBefore: true,
    usedCount: 2,
    usedInSameCourse: false,
    status: "Published",
    ...row,
  }
}

/**
 * 15 ready MC items at 5 points. One item in each section sits under the PBI
 * floor so Questions has a review row and banner without flooding reuse.
 */
function healthyDraftSeed(): AssessmentDetailPaperQuestion[] {
  return [
    okItem({ id: "q-ok-1", number: 1, stem: "A telemetry strip is regular at 72/min with a P wave before each QRS. Which structure is setting this rate?", title: "Sinus pacemaker", topic: "Cardiology", sectionId: "A", type: "Multiple Choice", difficulty: "Easy", difficultyPercentage: 84, pointBiserial: 0.42, discriminationIndex: 0.44, blooms: "Remembering", usedCount: 1 }),
    okItem({ id: "q-ok-2", number: 2, stem: "A harsh systolic murmur is loudest at the right upper sternal border and radiates to the carotids. Which valve lesion produces this sound?", title: "Aortic stenosis murmur", topic: "Cardiology", sectionId: "A", type: "Multiple Choice", difficulty: "Easy", difficultyPercentage: 78, pointBiserial: 0.4, discriminationIndex: 0.41, blooms: "Remembering", usedCount: 2 }),
    okItem({ id: "q-ok-3", number: 3, stem: "Chest pain started 30 minutes ago. The ECG shows ST elevation in II, III, and aVF. Which coronary territory is ischemic?", title: "Inferior STEMI territory", topic: "Cardiology", sectionId: "A", type: "Multiple Choice", difficulty: "Medium", blooms: "Applying", pointBiserial: 0.16, discriminationIndex: 0.18 }),
    okItem({ id: "q-ok-4", number: 4, stem: "A 79-year-old has a slow-rising carotid pulse and fainted while climbing stairs. Which valvular lesion is most likely?", title: "Delayed carotid upstroke", topic: "Cardiology", sectionId: "A", type: "Multiple Choice", difficulty: "Easy", difficultyPercentage: 88, pointBiserial: 0.45, discriminationIndex: 0.46, blooms: "Remembering", usedCount: 1 }),
    okItem({ id: "q-ok-5", number: 5, stem: "A client with an anterior-wall STEMI is going to PCI. Click the left anterior descending artery on the coronary diagram.", title: "LAD artery hotspot", topic: "Cardiology", sectionId: "A", type: "Hotspot", difficulty: "Medium", blooms: "Remembering" }),
    okItem({ id: "q-ok-6", number: 6, stem: "A client takes warfarin 5 mg nightly. Which laboratory value is used to adjust tomorrow's dose?", title: "Warfarin monitoring", topic: "Pharmacology", sectionId: "B", type: "Multiple Choice", difficulty: "Easy", difficultyPercentage: 82, pointBiserial: 0.48, discriminationIndex: 0.5, blooms: "Remembering", usedCount: 3 }),
    okItem({ id: "q-ok-7", number: 7, stem: "A client in anaphylaxis has wheezing and a BP of 72/40. Two minutes after IM epinephrine, which finding shows the dose is working?", title: "Epinephrine response", topic: "Pharmacology", sectionId: "B", type: "Multiple Choice", difficulty: "Medium", blooms: "Applying" }),
    okItem({ id: "q-ok-8", number: 8, stem: "Lisinopril was started 3 days ago. Today's potassium is 5.6 mEq/L. Which action is indicated?", title: "ACE inhibitor potassium", topic: "Pharmacology", sectionId: "B", type: "Multiple Choice", difficulty: "Medium", blooms: "Applying", pointBiserial: 0.14, discriminationIndex: 0.16 }),
    okItem({ id: "q-ok-9", number: 9, stem: "A client on digoxin reports yellow halos around lights and a pulse of 46. Which action is the priority?", title: "Digoxin toxicity", topic: "Pharmacology", sectionId: "B", type: "Multiple Choice", difficulty: "Hard", difficultyPercentage: 40, pointBiserial: 0.5, discriminationIndex: 0.52, blooms: "Applying", usedCount: 1 }),
    okItem({ id: "q-ok-10", number: 10, stem: "Furosemide 40 mg IV is ordered for flash pulmonary edema. Where in the nephron does this drug act?", title: "Loop diuretic site", topic: "Pharmacology", sectionId: "B", type: "Multiple Choice", difficulty: "Easy", blooms: "Remembering" }),
    okItem({ id: "q-ok-11", number: 11, stem: "A client has a temperature of 38.1 C, heart rate 102, respiratory rate 22, and a systolic BP of 78 after a fluid bolus. Which finding is treated first?", title: "Escalate first finding", topic: "Emergency Recognition", sectionId: "C", type: "Multiple Choice", difficulty: "Medium", difficultyPercentage: 80, pointBiserial: 0.41, discriminationIndex: 0.43, blooms: "Evaluating", usedCount: 2 }),
    okItem({ id: "q-ok-12", number: 12, stem: "Chest pain is now 4/10 after nitroglycerin, troponin is pending, and the ECG is unchanged. What is the next diagnostic step?", title: "Next diagnostic step", topic: "Diagnostic Reasoning", sectionId: "C", type: "Multiple Choice", difficulty: "Medium", blooms: "Analyzing", pointBiserial: 0.15, discriminationIndex: 0.17 }),
    okItem({ id: "q-ok-13", number: 13, stem: "Over 20 minutes BP falls from 118/72 to 86/50 and the client becomes newly confused. Which action is indicated?", title: "Rapid response trigger", topic: "Emergency Recognition", sectionId: "C", type: "Multiple Choice", difficulty: "Medium", difficultyPercentage: 86, pointBiserial: 0.43, discriminationIndex: 0.45, blooms: "Evaluating", usedCount: 1 }),
    okItem({ id: "q-ok-14", number: 14, stem: "A client with community-acquired pneumonia is going home on oral antibiotics. What return precautions do you give?", title: "Discharge safety net", topic: "Patient Communication", sectionId: "C", type: "Short Answer", difficulty: "Medium", blooms: "Applying" }),
    okItem({ id: "q-ok-15", number: 15, stem: "A 61-year-old has a temperature of 38.9 C, BP 86/48, and a new holosystolic murmur. Which action is the priority?", title: "Fever hypotension murmur", topic: "Diagnostic Reasoning", sectionId: "C", type: "Multiple Choice", difficulty: "Medium", blooms: "Analyzing" }),
  ]
}

export function healthyDraftPaper(): AssessmentDetailPaperQuestion[] {
  return withDraftAlignmentGaps(healthyDraftSeed())
}

export function isHealthyDraftAssessment(id: string | undefined): boolean {
  if (!id) return false
  if (id === HEALTHY_DRAFT_ASSESSMENT_ID || id === HEALTHY_DRAFT_ASSESSMENT_CODE) {
    return true
  }
  return id.endsWith(`-${HEALTHY_COURSE_OFFERING_SLUG}`)
}

/** Course Case Study Draft (`NURS-210-a5`). Hub `a5` is a Scheduled paper. */
export function isStressedDraftAssessment(id: string | undefined): boolean {
  return Boolean(id?.endsWith(`-${RESEARCH_PAPER_SLUG}`))
}

export function isPartialDraftAssessment(id: string | undefined): boolean {
  return Boolean(id?.endsWith(`-${PARTIAL_COURSE_OFFERING_SLUG}`))
}

export function isEmptyDraftAssessment(id: string | undefined): boolean {
  return Boolean(id?.endsWith(`-${EMPTY_COURSE_OFFERING_SLUG}`))
}

/**
 * N ready MC items at 5 points, healthy signals. Used for Scheduled (locked,
 * on quota) and for drafts that are not the all-negative Research Paper.
 */
export function healthyLockedPaper(count: number): AssessmentDetailPaperQuestion[] {
  const seed = healthyDraftSeed()
  const n = Math.max(1, count)
  return scalePaperMarksToCap(Array.from({ length: n }, (_, index) => {
    const sectionId = index < n / 3 ? "A" : index < (2 * n) / 3 ? "B" : "C"
    const pool = seed.filter((row) => row.sectionId === sectionId)
    const base = pool[index % pool.length] ?? seed[index % seed.length]
    return snapToSectionPlan(
      okItem({
        id: `q-ok-${index + 1}`,
        number: index + 1,
        stem: base.stem,
        title: base.title,
        type: base.type,
        topic: base.topic,
        sectionId,
        difficulty: base.difficulty,
        difficultyPercentage: base.difficultyPercentage,
        pointBiserial: 0.32,
        discriminationIndex: 0.34,
        blooms: base.blooms,
        usedCount: base.usedCount,
      }),
    )
  }))
}

/** Course-offering slug → status and paper size. Matches detail-page templates. */
const OFFERING_PAPER_PROFILE: Record<
  string,
  { status: string; questionCount: number }
> = {
  a1: { status: "Evaluating", questionCount: 25 },
  a2: { status: "Results Published", questionCount: 10 },
  a3: { status: "Evaluating", questionCount: 15 },
  a4: { status: "Live", questionCount: 50 },
  a5: { status: "Draft", questionCount: 20 },
  a6: { status: "Scheduled", questionCount: 30 },
  a7: { status: "Draft", questionCount: 15 },
  a8: { status: "Draft", questionCount: 25 },
  a9: { status: "Draft", questionCount: 0 },
}

function paperProfile(
  assessmentId: string,
): { status: string; questionCount: number } | undefined {
  const hub = MOCK_ASSESSMENTS.find(
    (row) => row.id === assessmentId || row.code === assessmentId,
  )
  if (hub) {
    return { status: hub.status, questionCount: hub.questionCount }
  }
  const slug = assessmentId.match(/-a\d+$/)?.[0]?.slice(1)
  if (!slug) return undefined
  return OFFERING_PAPER_PROFILE[slug]
}

function isCreatedAssessmentId(assessmentId: string): boolean {
  return readCreatedAssessments().some(
    (row) => row.id === assessmentId || row.code === assessmentId,
  )
}

export function paperForAssessment(
  assessmentId: string | undefined,
): AssessmentDetailPaperQuestion[] {
  if (!assessmentId) return defaultAssessmentPaper()
  // A drafted paper wins over the blueprint target. Creating with an
  // 80-question plan must not grow a 7-item first section into 80 items.
  const live = readLivePaper(assessmentId)
  if (live != null) return live
  if (isCreatedAssessmentId(assessmentId)) return []
  if (isHealthyDraftAssessment(assessmentId)) return healthyDraftPaper()
  if (isStressedDraftAssessment(assessmentId)) return defaultAssessmentPaper()
  if (isEmptyDraftAssessment(assessmentId)) return []
  if (isPartialDraftAssessment(assessmentId)) return partialBlueprintDraftPaper()
  const profile = paperProfile(assessmentId)
  if (profile?.status === "Scheduled") {
    return healthyLockedPaper(profile.questionCount)
  }
  if (profile?.status === "Draft") {
    return withDraftAlignmentGaps(healthyLockedPaper(profile.questionCount))
  }
  return defaultAssessmentPaper()
}
